/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useContext, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { Button } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { notifyError, notifySuccess } from "redux/actions/Notifications";
import LoadingPage from "components/common_components/LoadingPage";
import Grid from "@material-ui/core/Grid";
import PlayCircleFilledWhiteIcon from "@material-ui/icons/PlayCircleFilledWhite";
import { hasElements } from "utils/arrays";
import Chat from "components/Chat";
import ChatInitializerContext from "components/Chat/ChatInitializerContext";
import AppContext from "AppContext";
import { getAttributes } from "components/Chat/utils";
import { getEnvVar } from "utils/env";
import CustomersContext from "./CustomersContext";

// Twilio conversation states where the client can still chat (closed is terminal/read-only).
const USABLE_STATES = ["active", "inactive"];

function CustomerChatInitializer({ customer, customerPhoto }) {
    const dispatch = useDispatch();
    const { user } = useSelector((state) => state.Login);
    const [resources, setResources] = useState({ users: [] });
    const [clientConversations, setClientConversations] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isResolvingConversations, setIsResolvingConversations] = useState(false);

    const { Customers } = useContext(CustomersContext);

    const {
        api: {
            Settings: { Users },
        },
        messaging: {
            twilioConversations: { client },
        },
        conversations,
        setConversations,
        changeChatProfile,
    } = useContext(AppContext);

    const clientHasConversations = hasElements(clientConversations);

    // `customerPhoto` comes from Storage.downloadAsync as a Blob (used as-is by the customer form),
    // but the chat renders it via CSS `background-image: url(...)`, which needs a URL string —
    // hence converting the Blob to an object URL (cases already receive a URL from getDownloadURLAsync).
    const customerPhotoUrl = useMemo(
        () => (customerPhoto instanceof Blob ? URL.createObjectURL(customerPhoto) : customerPhoto),
        [customerPhoto]
    );

    useEffect(() => {
        return () => {
            if (customerPhoto instanceof Blob && customerPhotoUrl) {
                URL.revokeObjectURL(customerPhotoUrl);
            }
        };
    }, [customerPhotoUrl]);

    function getUsers() {
        Users.getAll({
            onSuccess: (data) => {
                setResources((prev) => ({ ...prev, users: data }));
            },
            onError: (error) => {
                setResources((prev) => ({ ...prev, users: [] }));
                dispatch(notifyError(error));
            },
        });
    }

    function getClientConversations() {
        setIsLoading(true);
        Customers.getConversations({
            id: customer.id,
            onSuccess: (data) => {
                const convs = data || [];
                setClientConversations(convs);
                // Keep the loader up until the async Twilio materialization resolves, so the UI
                // doesn't flash with incomplete state before the conversations load
                setIsResolvingConversations(hasElements(convs));
                setIsLoading(false);
            },
            onError: (error) => {
                setClientConversations([]);
                dispatch(notifyError(error));
                setIsLoading(false);
            },
        });
    }

    useEffect(() => {
        getUsers();
        getClientConversations();
        changeChatProfile(getEnvVar("VITE_CHAT_IDENTITY"));

        return () => {
            setResources({ users: [] });
            changeChatProfile(undefined);
        };
    }, []);

    useEffect(() => {
        const loadClientConversationsAsync = async () => {
            if (!client || !clientHasConversations) {
                setIsResolvingConversations(false);
                return;
            }

            // Materialize every backend conversation so we know its real Twilio state (source of
            // truth). Prefer getConversationBySid (sendable when the identity participates) and
            // fall back to peekConversationBySid (read-only) for closed/non-participant ones.
            const missing = clientConversations.filter((x) => !conversations.some((c) => c.sid === x.sid));
            if (!hasElements(missing)) {
                setIsResolvingConversations(false);
                return;
            }

            try {
                setIsResolvingConversations(true);
                const results = await Promise.allSettled(
                    missing.map(async (x) => {
                        try {
                            return await client.getConversationBySid(x.sid);
                        } catch (error) {
                            return await client.peekConversationBySid(x.sid);
                        }
                    })
                );
                const responses = results.filter((r) => r.status === "fulfilled" && r.value).map((r) => r.value);
                const failures = results.filter((r) => r.status === "rejected");
                if (hasElements(failures)) {
                    console.error("Error loading some client conversations", failures);
                }
                if (hasElements(responses)) {
                    setConversations((prev) => {
                        const toAdd = responses.filter((x) => !prev.some((c) => c.sid === x.sid));
                        return [...prev, ...toAdd];
                    });
                }
            } catch (error) {
                console.error("Error loading client conversations", error);
                dispatch(notifyError(error));
            } finally {
                setIsResolvingConversations(false);
            }
        };
        loadClientConversationsAsync();
    }, [clientConversations, client]);

    const startNewConversation = async () => {
        try {
            setIsLoading(true);
            const response = await Customers.startNewConversation(customer.id, user.id);
            const newConversation = response && response.data;
            if (newConversation) {
                dispatch(notifySuccess("Conversation started successfully!"));
                // Reload the full list (new active + closed history) instead of replacing with a
                // single conversation, so Twilio re-materializes and the render rules see everything
                getClientConversations();
            }
        } catch (error) {
            const customError = error && error.response && error.response.data && error.response.data.message;
            dispatch(notifyError(customError || error));
        } finally {
            setIsLoading(false);
        }
    };

    const resolveInternalUserLabel = (email) => {
        const authorUser = resources.users.find((u) => u.email === email);
        if (authorUser) {
            return `${String(authorUser.name).trim()}`;
        }
        return `Unknown User (${email})`;
    };

    const resolveClientUserLabel = () => {
        return customer.fullName;
    };

    const authorLabelResolver = (message) => {
        const { attributes } = message;
        if (attributes && attributes.senderUserEmail) {
            return resolveInternalUserLabel(attributes.senderUserEmail);
        }
        return resolveClientUserLabel();
    };

    const clientPhotoResolver = (conversation) => {
        const { type } = getAttributes(conversation);
        if (type === "external") {
            return customerPhotoUrl;
        }
    };

    const conversationNameResolver = async (conversation) => {
        const { type, clientName } = getAttributes(conversation);
        return type === "external" ? clientName : conversation.friendlyName;
    };

    // Twilio state is the source of truth. A conversation is "usable" (the client can still chat)
    // when Twilio reports it active or inactive; closed is terminal/read-only.
    const clientSids = clientConversations.map((c) => c.sid);
    const clientTwilioConvs = conversations.filter((c) => clientSids.includes(c.sid));

    const getActiveConversation = () => {
        return clientTwilioConvs.find((c) => USABLE_STATES.includes(c.state?.current)) || null;
    };

    const getHistoryConversations = () => {
        const active = getActiveConversation();
        return clientTwilioConvs.filter((c) => c.sid !== active?.sid);
    };

    const conversationsGetter = () => {
        const active = getActiveConversation();
        const history = getHistoryConversations();
        return active ? [active, ...history] : history;
    };

    const shownConversations = conversationsGetter();
    const hasUsableActive = Boolean(getActiveConversation());
    const isSingle = shownConversations.length === 1;

    return (
        <>
            {isLoading || isResolvingConversations ? (
                <LoadingPage />
            ) : (
                <Grid container spacing={3} style={{ justifyContent: "space-between", marginTop: 16 }}>
                    <Grid item xs={6}>
                        {!hasUsableActive && (
                            <Button
                                variant="text"
                                size="small"
                                color="primary"
                                startIcon={<PlayCircleFilledWhiteIcon />}
                                onClick={startNewConversation}
                            >
                                Start conversation
                            </Button>
                        )}
                    </Grid>
                    {hasElements(shownConversations) && (
                        <Grid item xs={12} className="chat-in-grid">
                            <ChatInitializerContext.Provider
                                value={{ authorLabelResolver, clientPhotoResolver, conversationNameResolver, conversationsGetter }}
                            >
                                <Chat isSingleConversation={isSingle} />
                            </ChatInitializerContext.Provider>
                        </Grid>
                    )}
                </Grid>
            )}
        </>
    );
}

CustomerChatInitializer.propTypes = {
    customer: PropTypes.object.isRequired,
    customerPhoto: PropTypes.any,
};

export default CustomerChatInitializer;
